import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { STAGES, type JumpTarget, type SetupAnswers, type StageKey } from '../../utils/clientSetup';
import { SetupPreview } from './SetupPreview';
import { EASE } from './SetupStart';

/**
 * The frame every setup question sits in.
 *
 * **THE ANIMATION IS KEYED ON THE STEP, NOT THE TITLE (16 Oct 2026).**
 * This was `key={title}`, which was fine while every title was a constant:
 * a new title meant a new screen, React remounted, and the fade-and-rise
 * played once on arrival. Then *Something else* was given a name field and
 * its title became whatever you type — so every keystroke looked like a
 * new screen. React threw the screen away and rebuilt it, the 0.28s
 * entrance replayed, and the preview's staggered tiles started again from
 * nothing. Tom caught it on video: the page flashing once per letter.
 *
 * The lesson is small and worth keeping: a `key` has to be the identity of
 * the thing, and a title stopped being that the moment it became editable
 * content. `screenKey` is the step number, which is what "which screen am
 * I on" actually means here.
 *
 * **ONE GRID, HELD FROM THE FIRST SCREEN TO THE LAST.** There used to be a
 * centred layout for the screens about people and a three-column split for
 * the rest; as a sequence that moved every element at once, twice. Now:
 * 4 columns of title, 5 of questions, 3 of their page.
 *
 * **The preview waits until there is a page to preview** — shown from
 * screen one it said "Your client / The job" and nothing else. Tom: *"fine,
 * but not needed on this screen."*
 *
 * **TWO LINES THE WHOLE FLOW HANGS FROM.**
 *   THE LABEL LINE    y=0 — the eyebrow left, "Their page" right. Both a
 *                     10px `.label` at line-height 1.2, so both 12px tall.
 *   THE CONTENT LINE  y=44 — the h1, the first question, the preview card.
 * Two offsets produce that one line: a column under a label drops 32px
 * (12 + 32 = 44); the middle column, which has no label, drops 44. The
 * block centres with `m-auto`, not `justify-center`, which would clip the
 * top of the taller screens.
 *
 * **CLOSE IS TOP-RIGHT, LIKE EVERY OTHER SCREEN.** It used to sit in a bar
 * across the bottom while SetupStart and SetupDone had it at the top. Worse,
 * it was close enough to the bottom edge that reaching for it surfaced the
 * macOS Dock and the click missed — the same family of bug as the iPhone
 * tab bar in index.css. Nothing of ours sits against an edge the OS claims.
 *
 * **BACK AND SKIP SIT UNDER THE TITLE** on a wide screen. Below `lg` the
 * columns stack and that would strand them above the question they go back
 * from, so there they stay in the actions row.
 */

/** Is there anything on their page yet? */
function hasPage(answers: SetupAnswers): boolean {
  return answers.kinds.length > 0 || answers.during.length > 0 || answers.outline.trim().length > 0 || answers.contacts.some(person => person.email.trim());
}

/** Down to the content line from a 12px label … */
const UNDER_LABEL = 'lg:mt-8';
/** … and from the top of the grid, for the column that has no label. */
const FROM_TOP = 'lg:mt-11';

/**
 * NOTHING IN THIS FLOW IS AUTOFILLED. 16 Oct 2026.
 *
 * Tom opened the flow and found the eyebrow reading DOGGED — a word
 * nobody at Portal had typed — and the due date already set to today, in
 * Chrome's amber autofill tint. The browser had filled the Client field
 * and the date from values it had seen in other unnamed text inputs.
 *
 * That is not cosmetic. The Client field names the project, the client
 * record and the page the client opens; the due date lands on every
 * deliverable of that kind. A browser guessing either one is a silent
 * data error the person setting the job up has no reason to look for.
 *
 * `autocomplete="off"` alone is not enough — Chrome overrides it when its
 * own heuristics decide a field looks like a name or an address. What
 * defeats the heuristic is giving every field a name it has never seen
 * before, so each one gets a random one. The 1Password and LastPass
 * opt-outs are there for the same reason, since both inject into bare
 * text inputs.
 *
 * Applied here rather than field by field so it covers every input in the
 * flow, including the ones added while a screen is open — another contact
 * row, another shoot day — and any field added later. Deliberately runs
 * on every render: it is a handful of nodes in one small subtree, and a
 * dependency list would miss exactly those late arrivals.
 */
function useNoAutofill(root: React.RefObject<HTMLElement>) {
  useEffect(() => {
    const fields = root.current?.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea');
    fields?.forEach(field => {
      field.setAttribute('autocomplete', 'off');
      field.setAttribute('autocorrect', 'off');
      field.setAttribute('data-1p-ignore', 'true');
      field.setAttribute('data-lpignore', 'true');
      if (!field.name) field.name = `setup-${Math.random().toString(36).slice(2, 10)}`;
    });
  });
}
export function SetupScreen({
  screenKey,
  stage,
  reached,
  eyebrow,
  title,
  answers,
  nextLabel = 'Continue',
  canNext = true,
  hint,
  onStage,
  onJump,
  onBack,
  onNext,
  onSkip,
  onClose,
  canBack = true,
  children
}: {
  /** Which screen this is. NEVER the title — see the note above. */
  screenKey: number;
  stage: StageKey;
  reached: StageKey[];
  eyebrow: string;
  title: string;
  answers: SetupAnswers;
  nextLabel?: string;
  canNext?: boolean;
  hint?: string;
  onStage: (stage: StageKey) => void;
  onJump?: (target: JumpTarget) => void;
  onBack: () => void;
  onNext: () => void;
  onSkip?: () => void;
  onClose: () => void;
  canBack?: boolean;
  children: React.ReactNode;
}) {
  const showPreview = hasPage(answers);
  const questions = useRef<HTMLDivElement | null>(null);
  useNoAutofill(questions);
  const backSkip = <div className="flex items-center gap-7" data-id="element-1376" data-mp="421sk02vpoy6">
      {canBack && <button type="button" onClick={onBack} className="label group inline-flex items-center gap-3 text-paper/45 transition-colors duration-200 hover:text-paper" data-id="element-1377" data-mp="3z5v0cuy12a9">
          <ArrowLeftIcon className="h-3 w-3 transition-transform duration-200 group-hover:-translate-x-1" strokeWidth={1.5} data-id="element-1378" data-mp="3l9nhyl6l61hp" />
          Back
        </button>}
      {onSkip && <button type="button" onClick={onSkip} className="label text-paper/45 transition-colors duration-200 hover:text-paper" data-id="element-1379" data-mp="3cgs9liltnpnp">
          Skip
        </button>}
    </div>;
  return <div className="fixed inset-0 z-50 flex flex-col bg-ink text-paper pt-safe" data-id="element-1380" data-mp="32kgs8e7dk9me">
      <div className="flex shrink-0 items-stretch border-b border-ruleDark" data-id="element-1381" data-mp="2xkb0yz50op4u">
        <nav aria-label="Stages" className="grid flex-1 grid-cols-3" data-id="element-1382" data-mp="2tiprlnroysze">
          {STAGES.map((item, index) => {
          const active = item.key === stage;
          const open = reached.includes(item.key);
          return <button key={item.key} type="button" disabled={!open} onClick={() => onStage(item.key)} aria-current={active ? 'step' : undefined} className={['relative flex items-baseline gap-3 px-5 py-4 text-left transition-colors duration-200 ease-out sm:px-10 sm:py-5', active ? 'text-paper' : open ? 'text-paper/45 hover:text-paper' : 'text-paper/20'].join(' ')} data-id="element-1383" data-mp="1d0pbp02ltf8v" data-mpi={index}>
                <span className="label" data-id="element-1384" data-mp="z8iz1nyntqe6">0{index + 1}</span>
                <span className="label hidden sm:inline" data-id="element-1385" data-mp="142dlv09kalhh">{item.label}</span>
                <span aria-hidden className={['absolute inset-x-0 -bottom-px h-px origin-left bg-paper transition-transform duration-300', active ? 'scale-x-100' : 'scale-x-0'].join(' ')} style={{
              transitionTimingFunction: 'cubic-bezier(0.23, 1, 0.32, 1)'
            }} data-id="element-1386" data-mp="6mgci8iwx0u5" />
              </button>;
        })}
        </nav>

        <button type="button" onClick={onClose} className="label shrink-0 px-6 text-paper/45 transition-colors duration-200 hover:text-paper sm:px-10" data-id="element-1387" data-mp="3a52mvo3vtihy">
          Close
        </button>
      </div>

      <div className="flex min-h-0 flex-1 overflow-y-auto" data-id="element-1388" data-mp="3f346ca57zjty">
        <motion.div key={screenKey} initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.28,
        ease: EASE
      }} className="m-auto grid w-full max-w-[1400px] gap-10 px-6 py-12 sm:px-12 lg:grid-cols-12 lg:gap-14 lg:py-16" data-id="element-1389" data-mp="3f2m50z3paff6">
          <header className="lg:col-span-4" data-id="element-1390" data-mp="6kpyi087vate">
            <p className="label m-0 text-mute" data-id="element-1391" data-mp="1or5u0fuv6n6">{eyebrow}</p>
            <h1 className={`mt-5 ${UNDER_LABEL} m-0 font-display text-[2.4rem] font-extralight leading-[0.95] tracking-tight sm:text-[3rem] lg:text-[3.6rem]`} data-id="element-1392" data-mp="1m5086ciz17l">
              {title}
            </h1>
            {(canBack || onSkip) && <div className="mt-12 hidden lg:block" data-id="element-1393" data-mp="bak27qxky1p7">{backSkip}</div>}
          </header>

          <div ref={questions} className={`${FROM_TOP} lg:col-span-5`} data-id="element-1394" data-mp="g9vikyu1b569">
            {children}
            {hint && <p className="mt-8 m-0 max-w-[46ch] text-[0.92rem] font-light leading-relaxed text-paper/35" data-id="element-1395" data-mp="2ii3py72mvhk">
                {hint}
              </p>}

            <div className="mt-12 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-1396" data-mp="3or02vdht9oef">
              <div className="order-2 sm:order-1 lg:hidden" data-id="element-1397" data-mp="3tp1ifog135l5">{backSkip}</div>
              <button type="button" onClick={onNext} disabled={!canNext} className={['label group order-1 inline-flex items-center gap-4 self-start border px-8 py-4 transition-colors duration-200 ease-out sm:order-2', canNext ? 'border-paper bg-paper text-ink hover:bg-transparent hover:text-paper' :
            /* No box, no fill: it should not read as a target. Opacity
               rather than a dim paper tint, because index.css lifts
               every faint paper tier to 55% for contrast and would
               undo it. */
            'cursor-not-allowed border-transparent text-paper opacity-40'].join(' ')} data-id="element-1398" data-mp="3fps46ubduljt">
                {nextLabel}
                <ArrowRightIcon className="h-3 w-3 transition-transform duration-200 group-enabled:group-hover:translate-x-1" strokeWidth={1.5} data-id="element-1399" data-mp="19ghcpeg26ya5" />
              </button>
            </div>
          </div>

          {/* The column is always here; it only fills once there is
              something worth looking at. */}
          <aside className="hidden lg:col-span-3 xl:block" data-id="element-1400" data-mp="3qoiof0qac1f0">
            {showPreview && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.3,
            ease: EASE
          }} data-id="element-1401" data-mp="3lqqz8eh0ytei">
                <p className="label m-0 text-paper/30" data-id="element-1402" data-mp="3vg0t9zvfim59">Their page · tap to jump</p>
                <div className={`mt-4 ${UNDER_LABEL} sticky top-6 flex min-h-[620px] flex-col bg-paper`} data-id="element-1403" data-mp="3vid5dweia2ae">
                  <SetupPreview answers={answers} onJump={onJump} data-id="element-1404" data-mp="32s4y4zlid2al" />
                </div>
              </motion.div>}
          </aside>
        </motion.div>
      </div>
    </div>;
}